import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { useToast } from '../../components/ui/ToastProvider'
import { formatBusinessDateTime } from '../../lib/datetime'
import { useTableSort } from '../../lib/useTableSort'
import DelegationCard from './components/DelegationCard'
import MyActionsTable from './components/MyActionsTable'
import MyPendingActionsCard from './components/MyPendingActionsCard'
import RemainingTimeCard from './components/RemainingTimeCard'
import StatusDistributionCard from './components/StatusDistributionCard'
import TopDelegateesCard from './components/TopDelegateesCard'
import ViewAllLink from './components/ViewAllLink'
import { ACTIONS_BASE, periodParams, viewAllHref } from './dashboardLinks'
import { queryKeyParts } from './hooks/useActionDashboard'
import { useCanPrioritize } from './hooks/useCanPrioritize'
import {
  MY_ACTIONS_PAGE_SIZE,
  useMyActionPrioritize,
  useMyReportActions,
} from './hooks/useMyReportActions'
import { useGrants } from '../../auth/useCurrentUser'
import { ACTION_LIST_ALL_SCOPE_GRANTS, GRANTS, type GrantCode } from '../../auth/grants'
import { type ActionScope } from '../actions/types'
import {
  ALL_STATUSES,
  DEFAULT_MY_SELECTION,
  isSameMySelection,
  parseMySelection,
  writeMySelection,
  type DashboardQuery,
  type MyActionSelection,
  type MyReport as MyReportData,
  type RemainingBucketKey,
  type ReminderResult,
} from './types'

interface Props {
  data: MyReportData | undefined
  loading: boolean
  /** Etkin filtre — alt tablo da panonun gördüğü dönemi hedeflesin diye kabuktan geçer. */
  query: DashboardQuery
}

/** Kapsam → görüntüleme yetkisi (aksiyon alt sayfa rotalarıyla aynı eşleme). */
const SCOPE_GRANTS: Record<ActionScope, readonly GrantCode[]> = {
  // "Tüm Görevler"i iki yetki açar — sınırlı hâli Genel Müdür'de (06.10.2026).
  all: ACTION_LIST_ALL_SCOPE_GRANTS,
  created: [GRANTS.ACTION_LIST_OWN_VIEW],
  assigned: [GRANTS.ACTION_LIST_ASSIGNED_VIEW],
  followed: [GRANTS.ACTION_LIST_FOLLOWED_VIEW],
  // Delegasyon bir SEKME DEĞİL (ACTION_TAB_SCOPES'ta yok) → bu eşleme onu hiç okumaz; girdi yalnız
  // Record tipini tamamlamak için var. Sayfanın gerçek koruması rotadadır (ASSIGNED VEYA OWN).
  delegation: [GRANTS.ACTION_LIST_ASSIGNED_VIEW],
}

/**
 * "Aksiyon Beklenen Maddeler" tablosunun **açılıştaki** düzeni (Raporum revizyonu §7 + mockup: satırlar
 * hedef teslim tarihine göre artan). Sunucu da aynı varsayılanı uygular; buradaki değer başlıktaki
 * sıralama okunun açılışta doğru kolonda görünmesini sağlar.
 */
const DEFAULT_SORT = { sortField: 'deadline', sortOrder: 1 } as const

/**
 * **Raporum** panosunun gövdesi (068 — analiz §2; 2026-08-19 revizyonu).
 *
 * <p><b>Yedi bölüm, iki kaynak:</b> altı özet bölümü tek sunucu yanıtından beslenir (zaman filtresi
 * değiştiğinde hepsi birlikte yenilenir); yedinci bölüm ("Aksiyon Beklenen Maddeler") sayfalı olduğu için
 * <b>ayrı</b> uçtan gelir → bir dilime tıklamak ya da sayfa çevirmek donut'ları yeniden çekmez.
 *
 * <p><b>Dört grafik tek tabloyu daraltır</b> (analiz §1.3/§2.3/§3.3/§4.4). Seçim <b>URL'de</b> durur
 * (`?myScope=&myBucket=&myRemaining=`) → yenilemede ve paylaşılan bağlantıda korunur. Üç boyut ayrı ayrı
 * yazılır çünkü grafikler farklı şeyler seçiyor: §2 <b>kapsamı</b>, §1/§3 <b>statüyü</b>, §4 <b>kalan süre
 * aralığını</b>. Aynı seçime yeniden tıklamak bölümün varsayılanına döner.
 *
 * <p>Kişi filtresi yoktur: kapsam sunucuda giriş yapan kullanıcıdan çözülür (analiz §2.1). Filtre çubuğu
 * ve "Son Güncelleme" üst kabukta ({@code HomeDashboard}) durur — iki pano onları paylaşır.
 */
export default function MyReport({ data, loading, query }: Readonly<Props>) {
  const { t } = useTranslation()
  const toast = useToast()
  const { hasGrant, hasAnyGrant } = useGrants()
  const [searchParams, setSearchParams] = useSearchParams()
  const selection = parseMySelection(searchParams)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState<number>(MY_ACTIONS_PAGE_SIZE)

  // Sıralama değişince sayfa BAŞA döner: 3. sayfadaki kayıt yeni düzende 1. sayfaya düşebilir.
  const resetPage = useCallback(() => setPage(0), [])
  const sorting = useTableSort(resetPage, DEFAULT_SORT)

  // Dönem değişince sayfa BAŞA döner. Aksi hâlde 3. sayfadayken dönem daraltıldığında sunucudan boş bir
  // sayfa gelir ve kullanıcı tablonun boşaldığını sanır — oysa veri 1. sayfadadır.
  const filterKey = queryKeyParts(query).join('|')
  useEffect(() => {
    setPage(0)
  }, [filterKey])

  const actions = useMyReportActions(query, selection, page, pageSize, sorting.sort)
  const prioritize = useMyActionPrioritize()
  const canPrioritize = useCanPrioritize()

  const total = actions.data?.page.totalElements ?? 0

  /**
   * Süzgeci URL'e yazar. Aynı süzgece yeniden tıklamak **varsayılana** döner (§K3 deseni: "seçimi
   * kaldır"); yeni bir süzgeçte sayfa başa alınır — yeni kümenin 3. sayfasında açılmak anlamsız olurdu.
   */
  const select = (next: MyActionSelection) => {
    const effective = isSameMySelection(selection, next) ? DEFAULT_MY_SELECTION : next
    setSearchParams(writeMySelection(searchParams, effective), { replace: true })
    setPage(0)
  }

  const selectStatus = (scope: MyActionSelection['scope']) => (bucket: string) =>
    select({ scope, bucket, remaining: null })

  const selectDelegation = (key: string) =>
    // Delegasyon Özeti'nin dilimleri statü DEĞİL kapsamdır → statü süzgeci bilinçli olarak kaldırılır.
    select({ scope: key === 'delegated' ? 'DELEGATED' : 'MINE', bucket: ALL_STATUSES, remaining: null })

  const selectRemaining = (bucket: RemainingBucketKey) =>
    // Kalan süre yalnız AÇIK kayıtlarda tanımlı → statü boyutu bölümün varsayılanında kalır.
    select({ scope: 'MINE', bucket: DEFAULT_MY_SELECTION.bucket, remaining: bucket })

  // Aksiyon alt sayfaları kendi görüntüleme yetkisiyle korunan AYRI rotalardır (2026-08-11); yetkisi
  // olmayan kullanıcıya "Tümünü Gör" GÖSTERİLMEZ (aksi halde rota `RequireGrant`'i "yetkiniz yok" ekranına
  // düşerdi — panolar sunucu-kapsamıyla dolduğundan liste yetkisinden bağımsız görünür, bağlantı ise ölürdü).
  const viewAllScope = (scope: ActionScope, blockFilter = '') =>
    SCOPE_GRANTS[scope].some((g) => hasGrant(g)) ? (
      <ViewAllLink to={viewAllHref(scope, query, blockFilter)} />
    ) : undefined

  /**
   * Tablonun başlık altı notu: hangi süzgeçle daraldığını söyler. Dilim adları **sunucudan** okunur
   * (grafiklerin legend'ıyla aynı kaynak) → istemci ikinci bir etiket eşlemesi tutmaz.
   */
  const tableNote = () => {
    if (selection.remaining != null) {
      return t('home.my.pending.noteRemaining', {
        name: t(`home.my.remaining.${selection.remaining}`),
        count: total,
      })
    }
    const scopeName = t(
      selection.scope === 'DELEGATED'
        ? 'home.my.delegation.delegated'
        : 'home.my.delegation.assignedToMe',
    )
    if (selection.bucket === ALL_STATUSES) {
      return t('home.my.pending.noteScope', { scope: scopeName, count: total })
    }
    const source =
      selection.scope === 'DELEGATED' ? data?.delegatedStatus : data?.statusDistribution
    const statusName =
      source?.slices.find((s) => s.bucket === selection.bucket)?.statusName ?? selection.bucket
    return t('home.my.pending.noteStatus', { scope: scopeName, name: statusName, count: total })
  }

  /**
   * Önceliklendirme sonucunu kullanıcıya çevirir — Yönetici Raporu'ndaki hatırlatma ile **aynı** üç
   * durum: hiç satır üretilmedi (atanan/şablon yok), sessiz saatte ötelendi, gönderildi. "Gönderildi"
   * deyip geçmek, bildirim saatlerce kuyrukta beklerken kullanıcıya yanlış bilgi vermek olurdu.
   */
  const reportResult = (result: ReminderResult) => {
    if (result.notificationCount === 0) {
      toast.warn(t('home.remind.none'))
      return
    }
    const counts = { actions: result.actionCount, notifications: result.notificationCount }
    if (result.deferredUntil) {
      // Saat KURUMSAL dilimde basılır: `deferredUntil` sunucunun kural gereği ürettiği bir andır;
      // hedef saat sistem parametresinden gelir, istemci onu varsaymaz.
      toast.warn(
        `${t('home.remind.queued', counts)} ${t('home.remind.quietHours', {
          time: formatBusinessDateTime(result.deferredUntil),
        })}`,
      )
      return
    }
    toast.success(t('home.remind.success', counts))
  }

  return (
    <>
      <div className="home-grid">
        <StatusDistributionCard
          distribution={data?.statusDistribution ?? { total: 0, slices: [] }}
          title={t('home.my.status.title')}
          hint={viewAllScope('assigned')}
          // `ALL` bir dilim anahtarı DEĞİLDİR: geçilirse `DonutChart` eşleşen dilim bulamaz ve ÜÇÜNÜ
          // birden soluklaştırır (grafik "devre dışı" görünür — Yönetici Raporu'nda 2026-08-07'de
          // gözlenen hata). Aynı sebeple kalan süre seçiliyken de bu donut'ta işaret yoktur.
          selectedBuckets={
            selection.scope === 'MINE' &&
            selection.remaining == null &&
            selection.bucket !== ALL_STATUSES
              ? [selection.bucket]
              : []
          }
          onSelect={selectStatus('MINE')}
          footNote={t('home.my.clickHint')}
          origin={data?.taskOrigin}
        />
        <DelegationCard
          delegation={
            data?.delegation ?? {
              total: 0,
              assignedToMeCount: 0,
              assignedToMePercent: 0,
              delegatedCount: 0,
              delegatedPercent: 0,
            }
          }
          /*
           * 2026-08-30 — bağlantı artık **Delegasyon** sayfasına gider (`/minutes/actions/delegation`).
           *
           * ÖNCESİ: seçili dilime göre tek bir listeye gidiyordu ("Bana Atananlar" ya da
           * "Oluşturduklarım?delegated=true"). Kullanıcı bildirimi: kart İKİ bilgiyi birden gösterirken
           * "Tümünü Gör" yalnız birini açıyordu. Yeni sayfa ikisini TEK listede, "Tür" kolonuyla verir
           * ve verisini kartla AYNI sorgudan alır (`MyActionRow`) → sayılar ayrışmaz.
           *
           * Yetki: sayfa yalnız KENDİ verinizi gösterdiği için iki yarıdan birini görebilen tamamını
           * görebilir → bağlantı `ASSIGNED` VEYA `OWN` ile çizilir. (Rotanın koruması da aynı ikili.)
           * Yönetici'de ASSIGNED var; "Oluşturduklarım" kaldırılınca kaybolan devir erişimi geri gelir.
           */
          hint={
            hasAnyGrant(GRANTS.ACTION_LIST_ASSIGNED_VIEW, GRANTS.ACTION_LIST_OWN_VIEW) ? (
              <ViewAllLink to={`${ACTIONS_BASE}/delegation?${periodParams(query)}`} />
            ) : undefined
          }
          // Yalnız "tüm statüler" süzgecinde işaretlidir: statü seçilmişken vurgulanan dilim §1/§3'tedir.
          selectedKey={
            selection.bucket === ALL_STATUSES
              ? selection.scope === 'DELEGATED'
                ? 'delegated'
                : 'assignedToMe'
              : null
          }
          onSelect={selectDelegation}
          footNote={t('home.my.clickHint')}
        />
      </div>

      <div className="home-grid">
        <StatusDistributionCard
          distribution={data?.delegatedStatus ?? { total: 0, slices: [] }}
          title={t('home.my.delegatedStatus.title')}
          // Bu blok "devrettiklerim" kümesini sayar; "Oluşturduklarım" kapsamı tek başına daha geniştir
          // (sorumlusuz ya da kendime atadıklarım da girer) → süzgeç bağlantıda taşınır (2026-08-21).
          hint={viewAllScope('created', '&delegated=true')}
          selectedBuckets={
            selection.scope === 'DELEGATED' && selection.bucket !== ALL_STATUSES
              ? [selection.bucket]
              : []
          }
          onSelect={selectStatus('DELEGATED')}
          footNote={t('home.my.clickHint')}
        />
        <RemainingTimeCard
          buckets={
            data?.remainingTime ?? [
              { bucket: 'D0_7', count: 0, percent: 0 },
              { bucket: 'D8_30', count: 0, percent: 0 },
              { bucket: 'D31_90', count: 0, percent: 0 },
              { bucket: 'D91_PLUS', count: 0, percent: 0 },
            ]
          }
          // Blok yalnız AÇIK ve termini bugün/sonrası olan kayıtları sayar (gecikmiş ve terminsiz
          // olanlar burada değil "Geciken Aksiyonlar"da) → bağlantı `upcoming` gönderir. Kullanıcı
          // bildirimi: kartta 3, hedef tabloda 5 satır görünüyordu (2026-08-21).
          hint={viewAllScope('assigned', '&upcoming=true')}
          selectedBucket={selection.remaining}
          onSelect={selectRemaining}
        />
      </div>

      <div className="home-grid">
        <MyActionsTable
          rows={data?.overdueActions ?? []}
          loading={loading}
          title={t('home.my.overdue.title')}
          emptyTitle={t('home.my.overdue.emptyTitle')}
          emptyDesc={t('home.my.overdue.emptyDesc')}
          // 2026-08-21: `criticalOrOverdue=true` idi — bloğun kendisi 2026-08-19'da "Kritik ve
          // Geciken"den "Geciken"e daralmıştı, bağlantı ise eski süzgeçte kalmış ve KRİTİK AMA
          // GECİKMEMİŞ kayıtları da listeliyordu. `overdue=true` blokla aynı kümeyi açar.
          footer={viewAllScope('assigned', '&overdue=true')}
        />
        <TopDelegateesCard
          rows={data?.topDelegatees ?? []}
          // §6 de "devrettiklerim" kümesinden beslenir (§3 ile aynı küme) → aynı süzgeç.
          footer={viewAllScope('created', '&delegated=true')}
        />
      </div>

      <MyPendingActionsCard
        rows={actions.data?.content ?? []}
        loading={actions.isLoading}
        total={total}
        page={page}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size)
          setPage(0)
        }}
        sorting={sorting}
        note={tableNote()}
        onPrioritize={
          canPrioritize
            ? (row) => prioritize.mutate(row.id, { onSuccess: reportResult })
            : undefined
        }
        prioritizing={prioritize.isPending}
      />
    </>
  )
}
